import '../App.css';
import { NavLink, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';

function Navbar() {
    const location = useLocation();
    const segmentRef = useRef(null);
    const buttonRefs = useRef([]);
    const [indicator, setIndicator] = useState({ left: 0, width: 0, opacity: 0 });

    const items = useMemo(() => ([
        { to: '/', label: 'Home' },
        { to: '/projects', label: 'Projects' },
        { to: '/parcours', label: 'Parcours' },
    ]), []);

    useEffect(() => {
        const segment = segmentRef.current;
        if (!segment) return;

        const measure = () => {
            const activeIndex = items.findIndex((item) => item.to === location.pathname);
            const fallbackIndex = location.pathname.startsWith('/Projects/') ? 1 : 0;
            const index = activeIndex >= 0 ? activeIndex : fallbackIndex;
            const activeButton = buttonRefs.current[index];
            if (!activeButton) return;

            const segmentRect = segment.getBoundingClientRect();
            const activeRect = activeButton.getBoundingClientRect();
            setIndicator({
                left: activeRect.left - segmentRect.left,
                width: activeRect.width,
                opacity: 1,
            });
        };

        measure();

        // Tailwind (CDN) and the Camcode font are applied after the first render,
        // so the layout keeps changing: re-measure whenever the buttons resize.
        const observer = new ResizeObserver(measure);
        observer.observe(segment);
        buttonRefs.current.forEach((button) => button && observer.observe(button));
        document.fonts?.ready.then(measure);
        window.addEventListener('resize', measure);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [items, location.pathname]);

    return (
        <nav className="fixed top-4 md:top-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-1.25rem)] max-w-3xl px-1">
            <div className="flex items-center justify-center gap-2 md:gap-3">
                <div
                    ref={segmentRef}
                    className="relative flex flex-1 items-center rounded-2xl border border-slate-300/60 bg-white/75 backdrop-blur-md shadow-[0_10px_30px_rgba(15,23,42,0.12)] p-1"
                    style={{ fontFamily: 'Camcode, sans-serif' }}
                >
                    <span
                        className="absolute top-1 bottom-1 rounded-xl bg-slate-900 transition-all duration-300 ease-out"
                        style={{
                            width: `${indicator.width}px`,
                            transform: `translateX(${indicator.left}px)`,
                            opacity: indicator.opacity,
                        }}
                    />

                    {items.map((item, index) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            ref={(el) => { buttonRefs.current[index] = el; }}
                            className={({ isActive }) => `relative z-10 flex-1 text-center px-4 py-2.5 text-sm md:text-base font-semibold rounded-xl transition-colors duration-200 ${isActive ? 'text-white' : 'text-slate-700 hover:text-slate-900'}`}
                            end={item.to === '/'}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </div>

                <a
                    href="https://github.com/Lukas-bt"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open GitHub profile"
                    className="h-11 w-11 md:h-12 md:w-12 rounded-2xl border border-slate-300/60 bg-white/75 backdrop-blur-md shadow-[0_10px_30px_rgba(15,23,42,0.12)] flex items-center justify-center text-slate-800 hover:text-black hover:-translate-y-0.5 transition-all"
                >
                    <svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor" aria-hidden="true">
                        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.44 9.8 8.21 11.39.6.11.79-.26.79-.57 0-.28-.01-1.2-.02-2.17-3.34.73-4.04-1.42-4.04-1.42-.55-1.39-1.34-1.76-1.34-1.76-1.1-.75.08-.74.08-.74 1.21.08 1.85 1.24 1.85 1.24 1.08 1.85 2.83 1.31 3.52 1 .11-.78.42-1.31.76-1.61-2.67-.3-5.47-1.34-5.47-5.95 0-1.31.47-2.38 1.23-3.23-.12-.3-.53-1.53.12-3.19 0 0 1-.32 3.3 1.23A11.5 11.5 0 0 1 12 5.8c1.02 0 2.04.14 3 .41 2.3-1.55 3.3-1.23 3.3-1.23.65 1.66.24 2.89.12 3.19.77.85 1.23 1.92 1.23 3.23 0 4.62-2.81 5.64-5.49 5.94.43.37.81 1.1.81 2.22 0 1.61-.01 2.9-.01 3.29 0 .31.2.69.8.57A12.01 12.01 0 0 0 24 12c0-6.63-5.37-12-12-12Z" />
                    </svg>
                </a>

                <a
                    href="https://www.linkedin.com/in/lukas-brillant-091192382"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open LinkedIn profile"
                    className="h-11 w-11 md:h-12 md:w-12 rounded-2xl border border-slate-300/60 bg-white/75 backdrop-blur-md shadow-[0_10px_30px_rgba(15,23,42,0.12)] flex items-center justify-center text-slate-800 hover:text-black hover:-translate-y-0.5 transition-all"
                >
                    <svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor" aria-hidden="true">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
                    </svg>
                </a>
            </div>
        </nav>
    );
}

export default Navbar;
